import React, { useEffect, useState } from "react";
import { Printer, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Section, Btn, Logo, ActionBadge, traceFromAudit } from "./ui";

function ReportTrace({ row }) {
  const t = traceFromAudit(row);
  return (
    <div className="mt-2 font-mono text-[10px] text-slate-400" data-testid="report-trace-meta">
      Metadata only ({row.decision}
      {row.held ? ", held" : ""}
      {row.masked_count ? `, masked×${row.masked_count}` : ""}
      ). Content is owner-scoped and not included in list/export.
      {t.entities.length > 0 ? ` Categories: ${t.entities.join(", ")}.` : ""}
    </div>
  );
}

export default function Report() {
  const { session } = useAuth();
  const [r, setR] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState(null);

  useEffect(() => { api.report(session.tenant.id).then(setR); }, [session.tenant.id]);
  useEffect(() => {
    api.audit(session.tenant.id, { limit: 200 }).then((rowsAll) => {
      const f = (rowsAll || []).filter((x) => x.decision === "MASK" || x.decision === "BLOCK" || x.held).slice(0, 6);
      setFlagged(f);
    });
  }, [session.tenant.id]);

  if (!r) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <Section
      title="Monthly Oversight Report"
      subtitle="Client-facing evidence of AI usage oversight. Supports audit readiness — not a certification of compliance."
      right={
        <Btn data-testid="print-report" onClick={() => window.print()} className="no-print">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </Btn>
      }
    >
      <div className="print-area mx-auto max-w-3xl rounded-xl border border-subtle bg-surface p-8">
        <div className="flex items-start justify-between border-b border-subtle pb-5">
          <div>
            <Logo className="h-9" />
            <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">AI Governance Oversight Report</p>
          </div>
          <div className="text-right">
            <div className="font-display text-lg font-bold">{r.tenant.name}</div>
            <div className="text-sm text-slate-400">{r.tenant.jurisdiction}</div>
            <div className="font-mono text-[11px] text-slate-500">Period: {r.period}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            ["Total requests", r.total],
            ["Blocked", r.blocked_requests],
            ["Masked", r.masked_requests],
            ["EU sovereign route", `${r.eu_route_pct}%`],
          ].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-subtle bg-elevated/50 p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
              <div className="mt-1 font-display text-2xl font-extrabold text-white">{v}</div>
            </div>
          ))}
        </div>

        {r.verification && (
          <div className="mt-6 rounded-lg border border-allow/30 bg-allow/5 p-5" data-testid="report-verification-section">
            <h4 className="mb-1 flex items-center gap-2 font-display text-sm font-bold">
              <ShieldCheck className="h-4 w-4 text-allow" /> Human Review of AI Output
            </h4>
            <p className="mb-4 text-[11px] text-slate-500">
              Evidence that AI responses were reviewed by a person before use. Only responses that produced
              AI output are counted; blocked or held requests are excluded.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["AI responses", r.verification.verifiable],
                ["Human-verified", r.verification.verified],
                ["Verified rate", `${r.verification.verified_pct}%`],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-subtle bg-elevated/50 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
                  <div className="mt-1 font-display text-xl font-extrabold text-white">{v}</div>
                </div>
              ))}
            </div>
            {r.verification.unverified > 0 && (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-warn" data-testid="report-unverified-note">
                <ShieldAlert className="h-3.5 w-3.5" /> {r.verification.unverified} AI response(s) were used or left without a recorded human review.
              </p>
            )}
          </div>
        )}

        <div className="mt-6 rounded-lg border border-brand/30 bg-brand/5 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-sm font-bold">AI Act Readiness Indicator</div>
              <p className="text-[11px] text-slate-500">Directional readiness signal derived from oversight activity. Not a legal certification.</p>
            </div>
            <div className="font-display text-4xl font-extrabold text-brand">{r.readiness_score}<span className="text-lg text-slate-500">/100</span></div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-elevated"><div className="h-2 rounded-full bg-brand" style={{ width: `${r.readiness_score}%` }} /></div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <h4 className="mb-2 font-display text-sm font-bold">Decision breakdown</h4>
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(r.by_decision).map(([k, v]) => (
                  <tr key={k} className="border-b border-subtle/50"><td className="py-1.5 text-slate-400">{k}</td><td className="py-1.5 text-right font-mono text-white">{v}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="mb-2 font-display text-sm font-bold">Top detected categories</h4>
            {r.top_categories.length ? (
              <table className="w-full text-sm">
                <tbody>
                  {r.top_categories.map((c) => (
                    <tr key={c.label} className="border-b border-subtle/50"><td className="py-1.5 text-slate-400">{c.label}</td><td className="py-1.5 text-right font-mono text-white">{c.count}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-sm text-slate-600">No categories recorded.</p>}
          </div>
        </div>

        <div className="mt-6">
          <h4 className="mb-2 font-display text-sm font-bold">Providers used</h4>
          <div className="flex flex-wrap gap-2">
            {r.providers.length ? r.providers.map((p) => (
              <span key={p.name} className="rounded-lg border border-subtle bg-elevated/50 px-3 py-1.5 text-xs text-slate-300">{p.name} · {p.count}</span>
            )) : <span className="text-sm text-slate-600">None.</span>}
          </div>
        </div>

        {r.sector === "hr" && r.bias && (
          <div className="mt-6 rounded-lg border border-block/30 bg-block/5 p-5" data-testid="report-bias-section">
            <h4 className="mb-1 flex items-center gap-2 font-display text-sm font-bold">
              <ShieldAlert className="h-4 w-4 text-block" /> Bias-Sensitive Attribute Oversight (HR)
            </h4>
            <p className="mb-4 text-[11px] text-slate-500">
              Evidence that bias-sensitive attributes (age, protected characteristics, photos) were
              detected and prevented from reaching an AI provider. Supports fair-hiring oversight — not a certification.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["Requests flagged", r.bias.flagged],
                ["Blocked", r.bias.blocked],
                ["Block rate", `${r.bias.block_rate}%`],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-subtle bg-elevated/50 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
                  <div className="mt-1 font-display text-xl font-extrabold text-white">{v}</div>
                </div>
              ))}
            </div>
            {r.bias.by_attribute.length > 0 && (
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="pb-1 text-left">Attribute detected</th>
                    <th className="pb-1 text-right">Occurrences</th>
                  </tr>
                </thead>
                <tbody>
                  {r.bias.by_attribute.map((a) => (
                    <tr key={a.label} className="border-b border-subtle/50">
                      <td className="py-1.5 text-slate-400">{a.label}</td>
                      <td className="py-1.5 text-right font-mono text-white">{a.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {r.seal && r.seal.chain_digest && (
          <div className="mt-6 rounded-lg border border-subtle bg-elevated/40 p-5" data-testid="report-seal">
            <h4 className="mb-1 flex items-center gap-2 font-display text-sm font-bold">
              <ShieldCheck className="h-4 w-4 text-brand" /> Audit Integrity Seal
            </h4>
            <p className="mb-4 text-[11px] text-slate-500">
              Tamper-evidence for auditors: a {r.seal.algorithm} over all {r.seal.record_count} audit
              records in this period. If any record is altered, inserted or removed, this seal digest changes.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Records chained", r.seal.record_count],
                ["Algorithm", "SHA-256"],
                ["First hash", r.seal.first_hash || "—"],
                ["Last hash", r.seal.last_hash || "—"],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-subtle bg-surface/60 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
                  <div className="mt-1 break-all font-mono text-[11px] text-slate-200">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg border border-brand/30 bg-brand/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Seal digest</div>
              <div data-testid="report-seal-digest" className="mt-1 break-all font-mono text-xs text-brand">{r.seal.chain_digest}</div>
            </div>
            <div className="no-print mt-3 flex flex-wrap items-center gap-3">
              <button
                data-testid="verify-seal-btn"
                onClick={async () => {
                  setVerifying(true); setVerifyRes(null);
                  try { setVerifyRes(await api.verifyReport(session.tenant.id, r.period)); }
                  finally { setVerifying(false); }
                }}
                disabled={verifying}
                className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand disabled:opacity-50"
              >
                {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                {verifying ? "Verifying…" : "Verify seal"}
              </button>
              {verifyRes && (
                verifyRes.valid ? (
                  <span data-testid="verify-result" className="flex items-center gap-1.5 rounded-lg border border-allow/40 bg-allow/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-allow">
                    <ShieldCheck className="h-3.5 w-3.5" /> Chain intact — {verifyRes.record_count} records re-verified, nothing altered
                  </span>
                ) : (
                  <span data-testid="verify-result" className="flex items-center gap-1.5 rounded-lg border border-block/40 bg-block/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-block">
                    <ShieldAlert className="h-3.5 w-3.5" /> Tampering detected — {verifyRes.mismatch_count} of {verifyRes.record_count} records altered
                  </span>
                )
              )}
            </div>
          </div>
        )}

        {flagged.length > 0 && (
          <div className="mt-6" data-testid="report-trace-appendix">
            <h4 className="mb-1 font-display text-sm font-bold">Flagged requests appendix</h4>
            <p className="mb-3 text-[11px] text-slate-500">
              Metadata for recent masked, blocked or held requests. Full conversation content is
              owner-scoped and not included in this report export.
            </p>
            <div className="space-y-3">
              {flagged.map((row) => (
                <div key={row.id} data-testid="report-trace-item" className="rounded-lg border border-subtle bg-elevated/30 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ActionBadge action={row.decision} small />
                      {row.held && <span className="font-mono text-[9px] uppercase tracking-wider text-warn">held</span>}
                      <span className="text-xs text-slate-300">{row.user_name}</span>
                    </div>
                    <span className="font-mono text-[10px] text-slate-500">{new Date(row.timestamp).toLocaleString("en-GB")}</span>
                  </div>
                  <ReportTrace row={row} />
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-8 border-t border-subtle pt-4 font-mono text-[10px] leading-relaxed text-slate-500">
          Generated by LucidGate on {new Date(r.generated_at).toLocaleString("en-GB")}. All data is fictional / synthetic.
          This report supports oversight and audit readiness and does not constitute a guarantee of legal compliance.
        </p>
      </div>
    </Section>
  );
}
